/**
 * V111: MM 팀 인력현황 카드 컴포넌트
 * V112: 팀 야근 합계 및 팀원 수 표시 추가
 * V113.4: 팀명 분리 + 인력 구성 상세 (팀인원/지원인원/대련인원)
 * V114: 인력현황 정의 전면 교체 (슬롯 기반 투입인력, 조직장 제외 유휴인력)
 * V114.1: UI 전면 재배치 + 중복투입 인원 이름 표시
 * V114.3: 투입/유휴/중복 카드 클릭 → 슬라이드 패널 표시
 *
 * 팀별 인력현황 요약 표시
 */

import React, { useState } from 'react';
import {
  Users,
  UserX,
  UserPlus,
  Clock,
  Handshake,
  Globe2,
} from 'lucide-react';
import { MmTeamSummary, formatOvertimeMinutes } from '../../../shared/types/mm';
import { MmEmployeeListPanel, MmEmployeePanelType } from './MmEmployeeListPanel';

interface MmTeamSummaryCardProps {
  /** 팀 데이터 */
  team: MmTeamSummary;
  /** V285: MM 기준 지사 (타지사 라벨 결정용) */
  contextCompanyCode?: 'KR' | 'CN';
}

/**
 * MM 팀 인력현황 카드
 */
export function MmTeamSummaryCard({ team, contextCompanyCode }: MmTeamSummaryCardProps) {
  // V114.3: 슬라이드 패널 표시 상태
  const [activePanelType, setActivePanelType] = useState<MmEmployeePanelType | null>(null);

  // V114: 타팀지원/타지사 존재 여부 (0이면 미표시)
  const hasSupportSlots = (team.assignedSupportSlots ?? 0) > 0;
  const hasDalianSlots = (team.assignedDalianSlots ?? 0) > 0;
  // V69 Phase 2: 기타(외부) 슬롯 존재 여부 (0이면 미표시)
  const hasOtherSlots = (team.assignedOtherSlots ?? 0) > 0;

  // V285: MM 기준 지사에 따라 타지사 라벨 결정
  const otherBranchLabel = contextCompanyCode === 'CN' ? '한국지사' : '대련지사';

  return (
    <div
      style={{
        padding: '16px',
        backgroundColor: 'white',
        border: '1px solid var(--color-gray-200)',
        borderRadius: '8px',
        marginBottom: '16px',
      }}
    >
      {/* 팀명 헤더 - V206: 이미지 제거, 텍스트 크기 30→26px */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid var(--color-gray-100)',
        }}
      >
        <h3
          style={{
            fontSize: '26px',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
            margin: 0,
          }}
        >
          {team.teamName}
        </h3>
      </div>

      {/* V114.1: 섹션 제목 "투입인력" - V206: 텍스트 크기 26→24px */}
      <h4
        style={{
          fontSize: '24px',
          fontWeight: 600,
          color: 'var(--color-gray-800)',
          margin: '0 0 8px 0',
        }}
      >
        투입인력
      </h4>

      {/* V114.1: 팀 총원 (일반 텍스트) */}
      <div
        style={{
          fontSize: '20px',
          color: 'var(--color-gray-600)',
          marginBottom: '12px',
        }}
      >
        팀 총원: {team.teamMemberCount ?? team.teamMemberIds.length}명
      </div>

      {/* V114.3: 주요 지표 가로 배치 (클릭 가능) */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        {/* V114.3: 투입인력 - 클릭 가능 (V116.4: 초록색으로 변경) - V206: 텍스트 20→18px */}
        <button
          type="button"
          onClick={() => setActivePanelType('assigned')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            backgroundColor: 'var(--color-green-50)',
            borderRadius: '8px',
            border: '1px solid var(--color-green-200)',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-green-100)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-green-50)';
          }}
        >
          <Users size={16} style={{ color: 'var(--color-green-600)' }} />
          <span style={{ fontSize: '18px', color: 'var(--color-green-700)' }}>
            투입인력
          </span>
          <strong
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color: 'var(--color-green-700)',
              marginLeft: '2px',
            }}
          >
            {team.totalAssignedSlots ?? 0}
          </strong>
        </button>

        {/* V114.3: 유휴인력 - 클릭 가능 - V206: 텍스트 20→18px */}
        <button
          type="button"
          onClick={() => setActivePanelType('idle')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            backgroundColor:
              (team.idleMembers ?? 0) > 0
                ? 'var(--color-yellow-50)'
                : 'var(--color-gray-50)',
            borderRadius: '8px',
            border:
              (team.idleMembers ?? 0) > 0
                ? '1px solid var(--color-yellow-200)'
                : '1px solid var(--color-gray-200)',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor =
              (team.idleMembers ?? 0) > 0
                ? 'var(--color-yellow-100)'
                : 'var(--color-gray-100)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor =
              (team.idleMembers ?? 0) > 0
                ? 'var(--color-yellow-50)'
                : 'var(--color-gray-50)';
          }}
        >
          <UserX
            size={16}
            style={{
              color:
                (team.idleMembers ?? 0) > 0
                  ? 'var(--color-yellow-600)'
                  : 'var(--color-gray-400)',
            }}
          />
          <span
            style={{
              fontSize: '18px',
              color:
                (team.idleMembers ?? 0) > 0
                  ? 'var(--color-yellow-700)'
                  : 'var(--color-gray-500)',
            }}
          >
            유휴인력
          </span>
          <strong
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color:
                (team.idleMembers ?? 0) > 0
                  ? 'var(--color-yellow-600)'
                  : 'var(--color-gray-400)',
              marginLeft: '2px',
            }}
          >
            {team.idleMembers ?? 0}명
          </strong>
        </button>

        {/* V114.3: 중복투입 - 클릭 가능 - V206: 텍스트 20→18px */}
        <button
          type="button"
          onClick={() => setActivePanelType('duplicated')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            backgroundColor:
              (team.duplicatedMembers ?? 0) > 0
                ? 'var(--color-blue-50)'
                : 'var(--color-gray-50)',
            borderRadius: '8px',
            border:
              (team.duplicatedMembers ?? 0) > 0
                ? '1px solid var(--color-blue-200)'
                : '1px solid var(--color-gray-200)',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor =
              (team.duplicatedMembers ?? 0) > 0
                ? 'var(--color-blue-100)'
                : 'var(--color-gray-100)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor =
              (team.duplicatedMembers ?? 0) > 0
                ? 'var(--color-blue-50)'
                : 'var(--color-gray-50)';
          }}
        >
          <UserPlus
            size={16}
            style={{
              color:
                (team.duplicatedMembers ?? 0) > 0
                  ? 'var(--color-blue-600)'
                  : 'var(--color-gray-400)',
            }}
          />
          <span
            style={{
              fontSize: '18px',
              color:
                (team.duplicatedMembers ?? 0) > 0
                  ? 'var(--color-blue-700)'
                  : 'var(--color-gray-500)',
            }}
          >
            중복투입
          </span>
          <strong
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color:
                (team.duplicatedMembers ?? 0) > 0
                  ? 'var(--color-blue-600)'
                  : 'var(--color-gray-400)',
              marginLeft: '2px',
            }}
          >
            {team.duplicatedMembers ?? 0}명
          </strong>
        </button>
      </div>

      {/* V114.1: 슬롯 구성 배지 (팀인원 / 타팀지원 / 대련지사) */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}
      >
        {/* 투입인력 소속 라벨 */}
        <span style={{ fontSize: '18px', color: 'var(--color-gray-600)', marginRight: '4px' }}>
          투입인력 소속 :
        </span>
        {/* 팀인원 슬롯: 항상 표시 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '5px 10px',
            backgroundColor: 'var(--color-primary-50)',
            borderRadius: '14px',
            fontSize: '18px',
            color: 'var(--color-primary-700)',
          }}
        >
          <Users size={12} />
          <span>팀인원</span>
          <strong>{team.assignedTeamSlots ?? 0}</strong>
        </div>

        {/* 타팀지원 슬롯: 0보다 클 때만 표시 */}
        {hasSupportSlots && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 10px',
              backgroundColor: 'var(--color-green-50)',
              borderRadius: '14px',
              fontSize: '18px',
              color: 'var(--color-green-700)',
            }}
          >
            <Handshake size={12} />
            <span>타팀지원</span>
            <strong>{team.assignedSupportSlots}</strong>
          </div>
        )}

        {/* 대련지사 슬롯: 0보다 클 때만 표시 */}
        {hasDalianSlots && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 10px',
              backgroundColor: 'var(--color-purple-50)',
              borderRadius: '14px',
              fontSize: '18px',
              color: 'var(--color-purple-700)',
            }}
          >
            <Globe2 size={12} />
            <span>{otherBranchLabel}</span>
            <strong>{team.assignedDalianSlots}</strong>
          </div>
        )}

        {/* V69 Phase 2: 기타(외부 인력) 슬롯 - 0보다 클 때만 표시 */}
        {hasOtherSlots && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 10px',
              backgroundColor: 'var(--color-gray-100)',
              borderRadius: '14px',
              fontSize: '18px',
              color: 'var(--color-gray-700)',
            }}
          >
            <Users size={12} />
            <span>기타</span>
            <strong>{team.assignedOtherSlots}</strong>
          </div>
        )}
      </div>

      {/* 팀 총 야근시간 */}
      {team.teamOvertimeMinutes > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: 'var(--color-blue-50)',
            border: '1px solid var(--color-blue-200)',
            borderRadius: '6px',
            fontSize: '18px',
            color: 'var(--color-blue-700)',
            marginBottom: '12px',
          }}
        >
          <Clock size={16} style={{ flexShrink: 0 }} />
          <span>팀 총 추가근무: {formatOvertimeMinutes(team.teamOvertimeMinutes)}</span>
          {/* V127: 정책 A 안내 문구 */}
          <span style={{ fontSize: '11px', color: 'var(--color-gray-500)', marginLeft: '4px' }}>
            (프로젝트 지정 건만 집계)
          </span>
        </div>
      )}

      {/* V114.3: 직원 목록 슬라이드 패널 */}
      {activePanelType && (
        <MmEmployeeListPanel
          type={activePanelType}
          teamName={team.teamName}
          employees={
            activePanelType === 'assigned'
              ? team.assignedEmployees
              : activePanelType === 'idle'
                ? team.idleEmployees
                : undefined
          }
          duplicatedEmployees={
            activePanelType === 'duplicated' ? team.duplicatedEmployees : undefined
          }
          // V117.1: 투입인력 패널에서 중복투입 배지 표시를 위해 BE 데이터 그대로 전달
          duplicatedUserIds={
            activePanelType === 'assigned' && team.duplicatedEmployees
              ? new Set(team.duplicatedEmployees.map((e) => e.userId))
              : undefined
          }
          onClose={() => setActivePanelType(null)}
        />
      )}
    </div>
  );
}
